import React, { useEffect, useState } from "react";
import { Button, Input } from "reactstrap";

const CHART_WIDTH = 1100;
const CHART_HEIGHT = 720;
const MAX_CANDLE_STEP = 18;
const CHART_MARGIN = {
  top: 12,
  right: 16,
  bottom: 40,
  left: 56,
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  const precision = Math.abs(numericValue) >= 20 ? 3 : 5;
  return numericValue.toFixed(precision);
};

const formatTimestampLabel = (timestamp) => {
  if (!timestamp) {
    return "";
  }

  const [datePart = "", timePart = ""] = String(timestamp).split(" ");
  return `${datePart.slice(5)} ${timePart.slice(0, 5)}`.trim();
};

const getLabelIndices = (totalCandles) => {
  if (totalCandles <= 1) {
    return [0];
  }

  const targetLabelCount = totalCandles <= 10 ? 3 : totalCandles <= 18 ? 4 : 5;
  const lastIndex = totalCandles - 1;

  return Array.from(
    new Set(
      Array.from({ length: targetLabelCount }, (_, index) =>
        Math.round((lastIndex * index) / (targetLabelCount - 1)),
      ),
    ),
  );
};

const TradeReplayChart = ({ replayData, currentFrameIndex }) => {
  const candles = replayData?.candles || [];
  if (!candles.length) {
    return null;
  }

  const levels = replayData?.levels || {};
  const currentIndex = Math.max(0, Math.min(currentFrameIndex, candles.length - 1));
  const plotWidth = CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
  const plotHeight = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
  const naturalStep = Math.min(plotWidth / candles.length, MAX_CANDLE_STEP);
  const bodyWidth = Math.max(3.5, Math.min(8.5, naturalStep * 0.5));
  const candleStep = Math.max(bodyWidth + 3, naturalStep * 0.8);
  const occupiedWidth = bodyWidth + candleStep * Math.max(candles.length - 1, 0);
  const candleStartX = CHART_MARGIN.left + (plotWidth - occupiedWidth) / 2;

  const priceValues = candles.flatMap((candle) => [
    candle.open,
    candle.high,
    candle.low,
    candle.close,
  ]);
  [
    levels.entry,
    levels.initial_stop_loss,
    levels.modified_stop_loss,
    levels.take_profit,
    levels.close_price,
  ].forEach((value) => {
    if (value !== null && value !== undefined && value !== "") {
      priceValues.push(Number(value));
    }
  });

  const minPrice = Math.min(...priceValues);
  const maxPrice = Math.max(...priceValues);
  const rawSpan = maxPrice - minPrice;
  const safeSpan = rawSpan > 0 ? rawSpan : Math.max(Math.abs(maxPrice) * 0.02, 0.001);
  const paddedMin = minPrice - safeSpan * 0.02;
  const paddedMax = maxPrice + safeSpan * 0.02;
  const paddedSpan = paddedMax - paddedMin;

  const getX = (index) => {
    return candleStartX + bodyWidth / 2 + candleStep * index;
  };

  const getY = (price) => {
    return CHART_MARGIN.top + ((paddedMax - price) / paddedSpan) * plotHeight;
  };

  const lineLevels = [
    {
      key: "entry",
      label: "Entry",
      value: levels.entry,
      color: "#6dd3ce",
    },
    {
      key: "initial-stop",
      label: "Initial SL",
      value: levels.initial_stop_loss,
      color: "#ff7b72",
      dash: "8 6",
    },
    {
      key: "moved-stop",
      label: "Moved SL",
      value:
        levels.modified_stop_loss !== null &&
        levels.modified_stop_loss !== undefined &&
        levels.modified_stop_loss !== levels.initial_stop_loss
          ? levels.modified_stop_loss
          : null,
      color: "#ffb454",
    },
    {
      key: "take-profit",
      label: "TP",
      value: levels.take_profit,
      color: "#69db7c",
      dash: "10 5",
    },
    {
      key: "close",
      label: "Close",
      value:
        replayData?.close_index !== null &&
        replayData?.close_index !== undefined &&
        currentIndex >= replayData.close_index
          ? levels.close_price
          : null,
      color: "#9ecbff",
      dash: "4 4",
    },
  ].filter((item) => item.value !== null && item.value !== undefined && item.value !== "");

  const verticalMarkers = [
    {
      key: "entry",
      label: "Entry",
      index: replayData?.entry_index,
      color: "#6dd3ce",
    },
    {
      key: "exit",
      label: "Exit",
      index: replayData?.close_index,
      color: "#9ecbff",
    },
  ].filter(
    (marker) =>
      marker.index !== null &&
      marker.index !== undefined &&
      marker.index >= 0 &&
      marker.index < candles.length &&
      marker.index <= currentIndex,
  );

  const priceGrid = Array.from({ length: 5 }, (_, index) => {
    const price = paddedMax - (paddedSpan * index) / 4;
    return {
      label: formatPrice(price),
      y: getY(price),
    };
  });

  const xLabelIndices = getLabelIndices(candles.length);

  return (
    <div className="trade-replay-chart-shell">
      <svg
        className="trade-replay-chart"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        role="img"
        aria-label="Trade replay candlestick chart"
      >
        <rect
          x={CHART_MARGIN.left}
          y={CHART_MARGIN.top}
          width={plotWidth}
          height={plotHeight}
          rx="18"
          className="trade-replay-chart__plot"
        />

        {priceGrid.map((gridLine) => (
          <g key={gridLine.label}>
            <line
              x1={CHART_MARGIN.left}
              y1={gridLine.y}
              x2={CHART_MARGIN.left + plotWidth}
              y2={gridLine.y}
              className="trade-replay-chart__grid-line"
            />
            <text
              x={CHART_MARGIN.left - 12}
              y={gridLine.y + 4}
              className="trade-replay-chart__axis-label"
              textAnchor="end"
            >
              {gridLine.label}
            </text>
          </g>
        ))}

        {lineLevels.map((level) => (
          <line
            key={level.key}
            x1={CHART_MARGIN.left}
            y1={getY(level.value)}
            x2={CHART_MARGIN.left + plotWidth}
            y2={getY(level.value)}
            stroke={level.color}
            strokeWidth="2"
            strokeDasharray={level.dash || undefined}
            opacity="0.9"
          />
        ))}

        {verticalMarkers.map((marker) => (
          <g key={marker.key}>
            <line
              x1={getX(marker.index)}
              y1={CHART_MARGIN.top}
              x2={getX(marker.index)}
              y2={CHART_MARGIN.top + plotHeight}
              stroke={marker.color}
              strokeWidth="2"
              strokeDasharray="5 5"
              opacity="0.85"
            />
            <text
              x={getX(marker.index)}
              y={CHART_MARGIN.top - 8}
              textAnchor="middle"
              className="trade-replay-chart__marker-label"
            >
              {marker.label}
            </text>
          </g>
        ))}

        {candles.map((candle, index) => {
          if (index > currentIndex) {
            return null;
          }

          const isBullish = candle.close >= candle.open;
          const candleColor = isBullish ? "#69db7c" : "#ff7b72";
          const x = getX(index);
          const openY = getY(candle.open);
          const closeY = getY(candle.close);
          const highY = getY(candle.high);
          const lowY = getY(candle.low);
          const bodyY = Math.min(openY, closeY);
          const bodyHeight = Math.max(Math.abs(closeY - openY), 1.25);

          return (
            <g key={`${candle.timestamp}-${index}`}>
              <line
                x1={x}
                y1={highY}
                x2={x}
                y2={lowY}
                stroke={candleColor}
                strokeWidth="1.1"
                strokeLinecap="round"
              />
              <rect
                x={x - bodyWidth / 2}
                y={bodyY}
                width={bodyWidth}
                height={bodyHeight}
                fill={isBullish ? "rgba(105, 219, 124, 0.28)" : "rgba(255, 123, 114, 0.26)"}
                stroke={candleColor}
                strokeWidth="1.1"
                rx="2"
              />
            </g>
          );
        })}

        {xLabelIndices.map((index) => (
          <text
            key={`x-label-${index}`}
            x={getX(index)}
            y={CHART_MARGIN.top + plotHeight + 26}
            textAnchor="middle"
            className="trade-replay-chart__axis-label"
          >
            {formatTimestampLabel(candles[index]?.timestamp)}
          </text>
        ))}
      </svg>
    </div>
  );
};

const TradeReplayPanel = ({
  replayData,
  loading,
  error,
  timeframe,
}) => {
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const candles = replayData?.candles || [];
  const safeIndex = candles.length
    ? Math.max(0, Math.min(currentFrameIndex, candles.length - 1))
    : 0;
  const currentCandle = candles[safeIndex];

  useEffect(() => {
    setCurrentFrameIndex(0);
    setIsPlaying(false);
  }, [replayData?.trade?.position_id, replayData?.timeframe, candles.length]);

  useEffect(() => {
    if (!isPlaying || candles.length === 0) {
      return undefined;
    }

    if (safeIndex >= candles.length - 1) {
      setIsPlaying(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setCurrentFrameIndex((previousIndex) => Math.min(previousIndex + 1, candles.length - 1));
    }, 550);

    return () => window.clearTimeout(timeoutId);
  }, [candles.length, isPlaying, safeIndex]);

  const handleTogglePlay = () => {
    if (!candles.length) {
      return;
    }

    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (safeIndex >= candles.length - 1) {
      setCurrentFrameIndex(0);
    }

    setIsPlaying(true);
  };

  const handleRestart = () => {
    setCurrentFrameIndex(0);
    setIsPlaying(false);
  };

  const handleScrub = (event) => {
    setCurrentFrameIndex(Math.max(0, Number(event.target.value) - 1));
    setIsPlaying(false);
  };

  const legendItems = [
    {
      key: "entry",
      label: "Entry",
      value: replayData?.levels?.entry,
      color: "#6dd3ce",
    },
    {
      key: "initial-stop",
      label: "Initial SL",
      value: replayData?.levels?.initial_stop_loss,
      color: "#ff7b72",
    },
    {
      key: "moved-stop",
      label: "Moved SL",
      value:
        replayData?.levels?.modified_stop_loss !== replayData?.levels?.initial_stop_loss
          ? replayData?.levels?.modified_stop_loss
          : null,
      color: "#ffb454",
    },
    {
      key: "take-profit",
      label: "Take Profit",
      value: replayData?.levels?.take_profit,
      color: "#69db7c",
    },
    {
      key: "close",
      label: "Close",
      value: replayData?.levels?.close_price,
      color: "#9ecbff",
    },
  ].filter((item) => item.value !== null && item.value !== undefined && item.value !== "");

  return (
    <div className="trade-replay-panel">
      <div className="trade-replay-toolbar">
        <div className="trade-replay-toolbar__group trade-replay-toolbar__group--primary">
          <span className="trade-replay-toolbar__eyebrow">{timeframe} Replay</span>
          <div className="trade-replay-toolbar__headline">
            <span>{replayData?.trade?.symbol || "Trade Replay"}</span>
            {replayData?.trade?.position_id ? (
              <span className="trade-replay-toolbar__meta">#{replayData.trade.position_id}</span>
            ) : null}
          </div>
        </div>

        <div className="trade-replay-toolbar__group">
          <span className="trade-replay-toolbar__label">Playback</span>
          <div className="trade-replay-toolbar__actions">
            <Button color="secondary" onClick={handleTogglePlay} disabled={!candles.length || loading}>
              {isPlaying ? "Pause" : "Play"}
            </Button>
            <Button color="secondary" onClick={handleRestart} disabled={!candles.length || loading}>
              Restart
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="trade-replay-state">
          <h4>Loading Replay</h4>
          <p>Fetching candle data for this trade.</p>
        </div>
      ) : error ? (
        <div className="trade-replay-state trade-replay-state--error">
          <h4>Replay Unavailable</h4>
          <p>{error}</p>
        </div>
      ) : !candles.length ? (
        <div className="trade-replay-state">
          <h4>No Candle Data</h4>
          <p>
            Add <code>{timeframe}</code> candles for this symbol and timeframe, then reopen the replay.
          </p>
        </div>
      ) : (
        <>
          <div className="trade-replay-status">
            <div className="trade-replay-status__item">
              <span className="trade-replay-status__label">Visible Candle</span>
              <span className="trade-replay-status__value">{formatTimestampLabel(currentCandle?.timestamp)}</span>
            </div>
            <div className="trade-replay-status__item">
              <span className="trade-replay-status__label">Progress</span>
              <span className="trade-replay-status__value">
                {safeIndex + 1} / {candles.length}
              </span>
            </div>
          </div>

          <TradeReplayChart replayData={replayData} currentFrameIndex={safeIndex} />

          <div className="trade-replay-scrubber">
            <div className="trade-replay-scrubber__header">
              <span className="trade-replay-toolbar__label">Replay Progress</span>
              <span className="trade-replay-scrubber__value">
                {safeIndex + 1} / {candles.length}
              </span>
            </div>
            <Input
              type="range"
              min="1"
              max={candles.length}
              value={safeIndex + 1}
              onChange={handleScrub}
            />
          </div>

          <div className="trade-replay-legend">
            {legendItems.map((item) => (
              <div className="trade-replay-legend__item" key={item.key}>
                <span
                  className="trade-replay-legend__swatch"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
                <span className="trade-replay-legend__label">{item.label}</span>
                <span className="trade-replay-legend__value">{formatPrice(item.value)}</span>
              </div>
            ))}
          </div>

          {replayData?.note ? (
            <p className="trade-replay-note">{replayData.note}</p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TradeReplayPanel;
